import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'antd';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import { useViewerStore } from '../../../../store/store';
import type { SystemDesignLossesResponse } from '../../../../services/oneButtonServices/designServices';
import {
    ACTIVE_LOSSES_VENDORS,
    type ActiveLossesVendor,
    vendorLossesValues,
} from '../../../../Constants';
import { isEnfinLossesSource } from './designLossesFinanceGate';
import {
    applyLossesVendor,
    resolveActiveLossesVendor,
} from '../../../../pre-contract/lib/preContractPartnerLosses';
import { persistLossesSource } from '../Losses-Toggle/persistLossesSource';
import { postPciLossesVendorToEmbed } from '../../../../pre-contract/lib/preContractEmbedBridge';
import SiteReportPopup from '../Modal/Modal-Content/SiteReportPopup';
import DashboardModal from './DashboardModal';
import './oneButton-css/DesignLossesModal.css';

const LOSS_LABELS: Record<string, string> = {
    SimulationName: 'Simulation',
    ShadingEngineName: 'Shading Engine',
    InverterEfficiency: 'Inverter Efficiency (%)',
    DcToAcRatio: 'DC to AC Ratio',
    WeatherDataset: 'Weather Dataset',
    Mismatch: 'Mismatch (%)',
    Connections: 'Connections (%)',
    LightInducedDegradation: 'Light induced degradation (%)',
    Soiling: 'Soiling (%)',
    Shading: 'Shading (%)',
    Availability: 'Availability (%)',
    Age: 'Age (%)',
    Snow: 'Snow (%)',
    Other: 'Other (%)',
    LossesSource: 'Losses Source',
};

function formatValue(v: unknown): string {
    if (v == null) return '—';
    if (typeof v === 'number') {
        return Number.isFinite(v) ? String(v) : '—';
    }
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    return String(v);
}

function lossesForVendor(
    losses: SystemDesignLossesResponse | null,
    vendor: ActiveLossesVendor,
): SystemDesignLossesResponse {
    const other = vendorLossesValues[vendor]?.find((row) => row.lossName === 'Other')?.percentage;
    return {
        ...(losses ?? {}),
        LossesSource: vendor === 'EnFin' ? 'EnFin' : 'oneDRAW',
        Other: other ?? (vendor === 'EnFin' ? '2' : '0'),
    };
}

export interface DesignLossesModalProps {
    open: boolean;
    onClose: () => void;
    losses: SystemDesignLossesResponse | null;
}

export const DesignLossesModal: React.FC<DesignLossesModalProps> = ({ open, onClose, losses }) => {
    const theme = useOneButtonStore((s) => s.theme);
    const selectedLossesVendor = useViewerStore((s) => s.selectedLossesVendor);
    const viewerVendor = resolveActiveLossesVendor(selectedLossesVendor, losses);
    const persistedVendor: ActiveLossesVendor = isEnfinLossesSource(losses)
        ? 'EnFin'
        : 'oneDRAW';
    const [draftVendor, setDraftVendor] = useState<ActiveLossesVendor>(viewerVendor);

    useEffect(() => {
        if (open) setDraftVendor(viewerVendor);
    }, [open, viewerVendor]);

    const previewLosses = useMemo(
        () => lossesForVendor(losses, draftVendor),
        [draftVendor, losses],
    );

    const entries = useMemo<[string, unknown][]>(() => {
        if (!previewLosses || typeof previewLosses !== 'object') return [];
        return Object.keys(LOSS_LABELS)
            .map((key): [string, unknown] => [key, previewLosses[key]])
            .filter(([, value]) => value !== undefined);
    }, [previewLosses]);

    const dirty = draftVendor !== persistedVendor || draftVendor !== viewerVendor;

    const commit = () => {
        try {
            persistLossesSource(draftVendor);
            applyLossesVendor(draftVendor, { force: true });
        } catch (error) {
            console.error('Design losses confirm failed', error);
        } finally {
            onClose();
        }
    };

    return (
        <DashboardModal
            open={open}
            title="Design losses"
            onClose={onClose}
            width={760}
            centered
            maskClosable={false}
            destroyOnClose
            theme={theme}
            className={`obd-design-losses-modal obd-theme-${theme}`}
            rootClassName={`obd-design-losses-modal-root obd-theme-${theme}`}
            footer={
                <>
                    <Button htmlType="button" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button htmlType="button" type="primary" onClick={() => commit()}>
                        Confirm
                    </Button>
                </>
            }
        >
            <div className="obd-losses-modal">
                {dirty ? (
                    <p className="obd-losses-modal__warn" role="status">
                        Confirming will refresh the site with {draftVendor} losses and update Losses
                        Source.
                    </p>
                ) : null}
                <div className="obd-losses-modal__source" role="group" aria-label="Losses source">
                    {ACTIVE_LOSSES_VENDORS.map((option) => (
                        <button
                            key={option.value}
                            type="button"
                            className={`obd-losses-modal__source-btn${
                                draftVendor === option.value ? ' is-selected' : ''
                            }`}
                            aria-pressed={draftVendor === option.value}
                            onClick={() => setDraftVendor(option.value)}
                        >
                            {option.value === 'EnFin' ? 'EnFin Losses' : 'oneDRAW Losses'}
                        </button>
                    ))}
                </div>
                <div className="obd-losses-modal__table">
                    {entries.length === 0 ? (
                        <p className="obd-losses-modal__empty">No losses data available.</p>
                    ) : (
                        entries.map(([key, value]) => (
                            <div key={key} className="obd-losses-modal__row">
                                <span className="obd-losses-modal__label">{LOSS_LABELS[key] ?? key}</span>
                                <span className="obd-losses-modal__value">{formatValue(value)}</span>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </DashboardModal>
    );
};

export default DesignLossesModal;
